import React, { useEffect, useState, useCallback } from 'react';
import { Check, X, Star, Loader2 } from 'lucide-react';
import { subApi } from '../api';
import { useAuth } from '../AuthContext';

const STATUS_LABEL = {
  trial: { label: 'Periodo di prova', cls: 'bg-amber-100 text-amber-800' },
  active: { label: 'Attivo', cls: 'bg-green-100 text-green-800' },
  expired: { label: 'Scaduto', cls: 'bg-red-100 text-red-700' },
  suspended: { label: 'Sospeso', cls: 'bg-gray-200 text-gray-700' },
};

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return '—'; }
}

export function Subscription() {
  const { isSuper, tenantId, refreshSubscription } = useAuth();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    subApi.get(isSuper ? tenantId : undefined).then(setSub);
  }, [isSuper, tenantId]);
  useEffect(() => { load(); }, [load]);

  const choose = async (plan) => {
    setBusy(plan);
    try {
      const s = await subApi.select(plan, isSuper ? tenantId : undefined);
      setSub(s);
      await refreshSubscription(isSuper ? 'super_admin' : 'tenant_admin', tenantId);
    } finally { setBusy(''); }
  };

  if (!sub) return <p className="text-sm text-paelbi-ink/50">Caricamento abbonamento...</p>;

  const st = STATUS_LABEL[sub.status] || STATUS_LABEL.expired;

  return (
    <div data-testid="subscription-page">
      <h1 className="font-display text-3xl text-paelbi-ink">Abbonamento</h1>
      <p className="mt-1 text-paelbi-ink/60">Gestisci il piano della tua struttura.</p>

      {/* Current status */}
      <div className="mt-6 rounded-2xl border border-paelbi-line bg-white p-6" data-testid="subscription-status">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm text-paelbi-ink/50">Piano attuale</p>
            <p className="font-display text-2xl capitalize text-paelbi-ink" data-testid="current-plan">{sub.plan}</p>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${st.cls}`} data-testid="current-status">{st.label}</span>
        </div>
        <div className="mt-4 grid gap-4 border-t border-paelbi-line pt-4 text-sm sm:grid-cols-3">
          {(sub.status === 'trial' || sub.status === 'active') && (
            <div><p className="text-paelbi-ink/50">Giorni rimanenti</p>
              <p className="text-lg font-semibold text-paelbi-brown" data-testid="days-remaining">{sub.days_remaining ?? '—'}</p></div>
          )}
          <div><p className="text-paelbi-ink/50">{sub.status === 'trial' ? 'Inizio prova' : 'Attivazione'}</p>
            <p className="text-paelbi-ink">{fmt(sub.status === 'trial' ? sub.trial_start : sub.subscription_start)}</p></div>
          <div><p className="text-paelbi-ink/50">Scadenza</p>
            <p className="text-paelbi-ink" data-testid="expiry-date">{fmt(sub.status === 'trial' ? sub.trial_end : sub.subscription_end)}</p></div>
        </div>
      </div>

      {/* Pricing cards */}
      <h2 className="mt-10 font-display text-xl text-paelbi-ink">Scegli il tuo piano</h2>
      <div className="mt-4 grid gap-6 md:grid-cols-3">
        {sub.catalog.map((p) => {
          const current = sub.status === 'active' && sub.plan === p.id;
          const featured = !!p.badge;
          return (
            <div key={p.id} data-testid={`plan-card-${p.id}`}
              className={`relative flex flex-col rounded-2xl border bg-white p-6 transition-shadow ${featured ? 'border-paelbi-brown shadow-card ring-1 ring-paelbi-brown/30' : 'border-paelbi-line'}`}>
              {p.badge && (
                <span className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-full bg-paelbi-brown px-3 py-1 text-xs font-semibold text-white" data-testid="plan-badge-plus">
                  <Star size={12} /> Più scelto
                </span>
              )}
              <p className="font-display text-xl text-paelbi-ink">{p.name}</p>
              <p className="mt-2"><span className="font-display text-3xl text-paelbi-ink">{p.currency}{p.price}</span>
                <span className="text-sm text-paelbi-ink/50"> {p.period}</span></p>
              <p className="mt-2 min-h-[40px] text-sm text-paelbi-ink/60">{p.tagline}</p>
              <ul className="mt-4 flex-1 space-y-2 text-sm">
                {p.includes.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-paelbi-ink/80"><Check size={16} className="mt-0.5 shrink-0 text-green-600" />{f}</li>
                ))}
                {p.excludes.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-paelbi-ink/40"><X size={16} className="mt-0.5 shrink-0" />{f}</li>
                ))}
              </ul>
              <button type="button" disabled={current || busy === p.id} onClick={() => choose(p.id)}
                data-testid={`select-plan-${p.id}`}
                className={`mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-btn text-sm font-semibold transition-colors disabled:opacity-60
                  ${featured ? 'bg-paelbi-brown text-white hover:bg-paelbi-brown-dark' : 'border border-paelbi-brown/40 text-paelbi-ink hover:border-paelbi-brown'}`}>
                {busy === p.id && <Loader2 size={16} className="animate-spin" />}
                {current ? 'Piano attuale' : 'Scegli ' + p.name}
              </button>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-center text-xs text-paelbi-ink/40">
        I pagamenti online saranno disponibili a breve. La selezione attiva subito il piano per la tua struttura.
      </p>
    </div>
  );
}

export default Subscription;
